import { getEventDisplayName } from '../../shared/utils/notification-display.util';
import { subscriptionStatusLabel } from '../../shared/utils/subscription-pause.copy';
import {
  ActivityEvent,
  ActivityType,
  DrbSyncReason,
  PlateAlert,
  PlateLookupMembership,
  PurchaseRow,
} from '../services/plate-lookup.service';

/**
 * Wording and formatting for the Plate Lookup page (CWS-1703).
 *
 * Labels reuse what the staff app already shows: gate event names come from the
 * Notifications list (`getEventDisplayName`), subscription statuses from the
 * Subscriptions tab (`subscriptionStatusLabel`). Times are shown in the shop's own
 * time zone, as the Notifications list does.
 */

export const DASH = '—';
const FALLBACK_TIMEZONE = 'America/Los_Angeles';

function format(value: string | null | undefined, tz: string | null | undefined, opts: object) {
  if (!value) return DASH;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return DASH;
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: tz || FALLBACK_TIMEZONE, ...opts }).format(
      date
    );
  } catch {
    // A location saved with a time zone the browser does not know must not blank the page.
    return new Intl.DateTimeFormat('en-US', { timeZone: FALLBACK_TIMEZONE, ...opts }).format(date);
  }
}

/** "Sep 27, 5:58 PM" */
export const formatDateTime = (value: string | null | undefined, tz?: string | null) =>
  format(value, tz, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

/** "Oct 22, 2026" */
export const formatDate = (value: string | null | undefined, tz?: string | null) =>
  format(value, tz, { month: 'short', day: 'numeric', year: 'numeric' });

/** "October 2026", for grouping the Activity list. */
export const formatMonth = (value: string | null | undefined, tz?: string | null) =>
  format(value, tz, { month: 'long', year: 'numeric' });

export const ACTIVITY_LABEL: Record<ActivityType, string> = {
  lpr_scanned: 'LPR Scanned',
  membership_redeemed: 'Membership Redeemed',
  rewash: 'Rewash',
  purchase: 'Purchase',
  membership_renewal: 'Membership Renewal',
  membership_started: 'Membership Started',
  membership_cancelled: 'Membership Cancelled',
  way_plus: 'Way+',
};

/** Filter chips on the Activity tab; each covers one or more event types. */
export const ACTIVITY_FILTERS: { key: string; label: string; types: ActivityType[] }[] = [
  { key: 'lpr', label: 'LPR Scanned', types: ['lpr_scanned'] },
  { key: 'redeemed', label: 'Membership Redeemed', types: ['membership_redeemed'] },
  { key: 'rewash', label: 'Rewash', types: ['rewash'] },
  {
    key: 'purchase',
    label: 'Purchase & Renewal',
    types: ['purchase', 'membership_renewal', 'way_plus'],
  },
  { key: 'membership', label: 'Membership', types: ['membership_started', 'membership_cancelled'] },
];

/** The gate event name exactly as the Notifications list shows it. */
export function gateEventLabel(event: string | null | undefined, isCompleted = false): string {
  return event ? getEventDisplayName(event, { event, is_completed: isCompleted }) : DASH;
}

export function sourceLabel(source: string | null | undefined): string {
  if (!source) return DASH;
  return source.toLowerCase() === 'way' ? 'Way' : source.toUpperCase();
}

/** Upper-case letters and digits, with O read as 0: DRB stores a vanity O as 0. */
const plateMatchKey = (plate: string) =>
  plate
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase()
    .replace(/O/g, '0');

/** A vendor code that names a car, as DRB writes it: `87878D3-CA`. Same shape the gate uses. */
const PLATE_CODE = /^([A-Za-z0-9]{2,8})-[A-Za-z]{2}$/;

/**
 * The car a plan covers: the plate its code names, else the car on its order. Null when neither
 * says (a plan bought without a car), so the page never guesses.
 */
export function planPlate(m: PlateLookupMembership): string | null {
  const fromCode = PLATE_CODE.exec(m.source_id ?? '')?.[1];
  const plate = fromCode ?? m.order_vehicle_plate ?? null;
  return plate ? plate.replace(/[^A-Za-z0-9]/g, '').toUpperCase() : null;
}

/**
 * Whether a plan covers the searched plate. A family link is read for the searched car only, and
 * a plan whose car is unknown stays with the searched car rather than being hidden by a guess.
 */
export function planCoversPlate(m: PlateLookupMembership, searchedPlate: string): boolean {
  if (m.family_link_status === 'active') return true;
  const plate = planPlate(m);
  return !plate || plateMatchKey(plate) === plateMatchKey(searchedPlate);
}

/** Status as the Subscriptions tab says it, with the "active but ended" trap called out. */
export function membershipStatusLabel(m: PlateLookupMembership): string {
  if (!m.is_active && m.period_ended && (m.status === 'active' || m.status === 'trialing')) {
    return 'Active, period ended';
  }
  if (!m.is_active && m.status === 'active') return 'Expired';
  if (m.status === 'past_due') return 'Past due';
  return subscriptionStatusLabel(cardStatusKey(m));
}

/**
 * subscriptionStatusLabel takes the card's status keys, not the table's raw status:
 * a live trial reads as active, a pending cancellation and a pause get their own
 * keys, and the table spells `canceled` with one l.
 */
function cardStatusKey(m: PlateLookupMembership): string {
  if (m.status === 'canceled') return 'cancelled';
  if (!m.is_active) return m.status;
  if (m.cancel_at_period_end) return 'cancelling';
  if (m.pause_status === 'active') return 'paused';
  if (m.pause_status === 'scheduled') return 'scheduled_pause';
  return m.status === 'trialing' ? 'active' : m.status;
}

export function membershipTone(m: PlateLookupMembership): 'good' | 'warn' | 'grey' {
  if (m.is_active) return 'good';
  return m.status === 'active' || m.status === 'trialing' ? 'warn' : 'grey';
}

const plateWithConfidence = (plate: string, confidence: number | null | undefined) =>
  confidence === null || confidence === undefined ? plate : `${plate} (${Math.round(confidence)}%)`;

const PURCHASE_KIND: Record<string, string> = {
  single_wash: 'Single Wash',
  membership: 'Monthly membership',
  prepaid: 'Prepaid',
  way_pass: 'Way pass',
  rewash: 'Rewash',
  renewal: 'Membership Renewal',
  plan_change: 'Plan change',
  way_plus: 'Way+',
};

export const purchaseKindLabel = (kind: string) => PURCHASE_KIND[kind] ?? kind;

export function money(amount: number | null | undefined): string {
  return amount === null || amount === undefined || Number.isNaN(amount)
    ? DASH
    : `$${amount.toFixed(2)}`;
}

/**
 * A membership start or end written by a vendor roster import, not by anything that happened at
 * the shop: vendor memberships are only ever created and closed by the import.
 */
export function isImportActivity(e: ActivityEvent): boolean {
  const vendorSource = Boolean(e.source) && e.source!.trim().toLowerCase() !== 'way';
  return vendorSource && (e.type === 'membership_started' || e.type === 'membership_cancelled');
}

/** Hover text on the Import tag, naming the vendor whose roster wrote the row. */
export const importActivityTitle = (e: ActivityEvent) =>
  `Recorded by the ${sourceLabel(e.source)} roster import`;

/** The one-line description of an Activity row. */
export function activityDetail(e: ActivityEvent): string {
  switch (e.type) {
    case 'membership_redeemed': {
      const parts = [e.planName, e.code].filter(Boolean);
      if (e.scan) parts.push(`Plate read ${plateWithConfidence(e.scan.plate, e.scan.confidence)}`);
      else if (!e.otherVehicle) parts.push('No LPR scan');
      if (e.vehiclePlate) parts.push(`Vehicle ${e.vehiclePlate}`);
      return parts.join(' · ');
    }
    case 'lpr_scanned': {
      const parts = [gateEventLabel(e.event, e.isCompleted)];
      if (e.scan) parts.push(plateWithConfidence(e.scan.plate, e.scan.confidence));
      if (e.code) parts.push(e.code);
      parts.push('Not redeemed');
      return parts.join(' · ');
    }
    case 'rewash': {
      const parts = ['Rewash order'];
      if (e.planName) parts.push(e.planName);
      if (e.scan) parts.push(`Plate read ${e.scan.plate}`);
      return parts.join(' · ');
    }
    case 'purchase':
      return [purchaseKindLabel(e.kind ?? ''), e.planName, money(e.amount), e.paymentStatus]
        .filter(Boolean)
        .join(' · ');
    case 'membership_renewal':
      return [e.planChange ? 'Plan change' : 'Renewed', e.planName, e.code, money(e.amount)]
        .filter(Boolean)
        .join(' · ');
    case 'membership_started':
    case 'membership_cancelled':
      return [e.planName, e.code, sourceLabel(e.source)].filter(Boolean).join(' · ');
    case 'way_plus':
      return ['Way+ enrolment', e.status].filter(Boolean).join(' · ');
    default:
      return DASH;
  }
}

export const DRB_REASON_HINT: Record<DrbSyncReason, string> = {
  active_membership: 'Active Subscription found',
  membership_expired: 'Subscription ended',
  no_membership: 'No subscription',
  not_in_our_records: 'Not in our records',
};

/** "Vehicle mismatch · Champs": with every shop shown, the title says which shop it is. */
const titleAt = (title: string, alert: PlateAlert) =>
  alert.shopName ? `${title} · ${alert.shopName}` : title;

/** Title and line for each alert, in the app's own words. */
export function alertCopy(alert: PlateAlert): {
  title: string;
  text: string;
  tone: 'warn' | 'info';
} {
  switch (alert.type) {
    case 'vehicle_mismatch':
      return {
        title: titleAt('Vehicle mismatch', alert),
        text: `Plate read ${alert.readPlate}, ${alert.count} wash${alert.count === 1 ? '' : 'es'} redeemed on vehicle ${alert.vehiclePlates.join(', ')}.`,
        tone: 'warn',
      };
    case 'duplicate_customer':
      return {
        title: 'Duplicate customer',
        text: `${alert.count} customer records for this plate${alert.shopName ? ` at ${alert.shopName}` : ''}: ${alert.codes.join(', ') || DASH}.`,
        tone: 'warn',
      };
    case 'close_washes':
      return {
        title: titleAt('Multiple washes same day', alert),
        text: alert.days
          .map(d => `${formatDate(d.at, d.timezone)}: ${d.count} washes in ${d.minutes} min`)
          .join(' · '),
        tone: 'info',
      };
    case 'active_period_ended':
      return {
        title: titleAt('Active, period ended', alert),
        text: `Renewal date has passed: ${alert.codes.join(', ') || DASH}.`,
        tone: 'warn',
      };
    default:
      return { title: '', text: '', tone: 'info' };
  }
}

export function purchaseRowLabel(row: PurchaseRow): string {
  return purchaseKindLabel(row.kind);
}
